/* Defers offscreen PDP gallery slides from firing network requests on initial load
   (issue #311, spec live-theme/pdp-performance/IMAGE-SPEC.md §3.3, Phase 2 of the PDP
   performance wave - ships in its own PR after the Phase 1 issues verify live).

   PROBLEM: snippets/media.liquid renders every product.media item as an in-flow
   Flickity slide at parse time. Flickity lays the whole track out before JS
   repositions slides off-screen, so the browser's viewport-distance heuristic treats
   most slides as "near viewport" and fetches them regardless of loading="lazy" -
   measured live 2026-09-09: 103 of 283 gallery <img> tags fired on the Ultimax PDP's
   initial desktop load (18.5MB). The bundle page's colorway alternates ride this same
   [data-product-slideshow] / product__media path.

   FIX: media.liquid now renders only the first 3 slides (position 1's existing
   eager/preload/fetchpriority-high handling is untouched) as real <img> elements.
   Every later slide is wrapped in <template data-deferred-media> - inert markup the
   browser never fetches. This file is the additive hydration layer that swaps a
   slide's <template> for its real content:
     1. as the carousel's selected index comes within HYDRATE_AHEAD slides of it
        (Flickity `settle` / `dragEnd` / `change`, a `resize` listener, a `load`
        listener, and a 700ms delayed fallback pass - the exact re-sync trigger list
        already proven for this carousel by window.__tcGalleryExposureSync in
        sections/product.liquid, reached the same way that script reaches Flickity:
        window.themeVendor.Flickity.default, resolved via its .data(element) lookup,
        never a global Flickity constructor);
     2. on pointerdown / touchstart / focusin anywhere in the gallery - a hard backstop
        for interactions that can reach a slide faster than the proximity walk above (a
        thumbnail jump, tabbing, a fast multi-slide drag). Galleries that never run the
        fade slider hydrate every remaining slide here; fade-capable galleries re-sync
        and hydrate the selected window instead (issue #465, below);
     3. synchronously in a capture-phase click listener on the gallery's own zoom
        button(s) - belt-and-suspenders for the PhotoSwipe zoom modal, which must never
        open on a stale/blank image. Same split as backstop 2.

   WHY THIS IS SAFE FOR ZOOM: media.liquid wraps ONLY the <img> render call, not the
   [data-zoom-button] markup that follows it, and not the [data-media-id] slide wrapper
   around both - so every zoom button on the page is a real DOM node from initial parse,
   present in the querySelectorAll pass theme.js's productPhotoswipeZoom() runs once at
   page init to attach its click listeners. This script therefore never needs to
   re-attach a click listener theme.js already owns. PhotoSwipe's own item list is built
   from [data-product-json] (confirmed by reading assets/theme.js directly - the
   zoomButton click handler loops json.media, not gallery DOM <img> elements), so the
   zoom modal's image source and dimensions never depend on whether this script has
   hydrated a slide. Hydrating before the click reaches theme.js's listener only
   improves incidental behavior that DOES read the live DOM: getAlt() in that same
   handler prefers the gallery's own (alt-text-filter-stripped) <img alt>, falling back
   to the unfiltered product-JSON alt when no <img> exists yet.

   WHY THIS IS SAFE FOR window.__tcGalleryExposureSync (PR #150, DO NOT REPATCH): that
   script is never touched or called by this file. It already runs a MutationObserver
   with { subtree: true, childList: true } on the exact same [data-product-slideshow]
   element this script hydrates into, so replacing a <template> with real content is
   picked up as an ordinary childList mutation and re-runs that script's own geometry
   pass automatically - no synthetic event needs to be dispatched, and nothing in this
   file writes an aria-hidden/tabindex attribute itself.

   Namespace: window.tcGalleryDefer. No dependencies. Additive only - never mutates
   theme.js, never mutates __tcGalleryExposureSync, never removes a <template> it did
   not itself insert content from.

   FIXES FROM PR #333 REVIEW (Codex connector + a local Codex CLI pass both flagged
   the same three gaps independently):
   1. Non-carousel desktop layouts (Stacked/Grid/Mosaic - theme.js's createDesktopSlider
      returns early when desktopStyle=="none", so no Flickity instance is ever created)
      had no hydration path but the two hard backstops: currentIndex() always fell back
      to slide 0, so ordinary mouse-wheel scrolling past slide 3 left every later slide
      an inert <template> for the rest of the visit. Fixed with an IntersectionObserver
      per slide (observeSlides()) - works alongside, not instead of, the Flickity
      proximity walk, and is a no-op once a slide is already hydrated.
   2. The quickview drawer's gallery markup is injected via
      `this.modalContent.innerHTML = ...` (theme.js's getQuickviewHTML) INTO a modal
      wrapper that was already appended to document.body at Alpine init time - so the
      bodyObserver below (childList on document.body only, not subtree) never sees that
      nested mutation and setupInstance() was never called on it. Fixed by also
      listening for theme.js's own `theme:quickview:media` CustomEvent (dispatched,
      bubbling, immediately after that innerHTML assignment, in the same .then()) and
      rescanning on it.
   3. The bindFlickity() retry ladder (250ms x 20 = ~5s) is a one-shot: once exhausted,
      nothing calls bindFlickity() again. theme.js's Media class recreates its Flickity
      instance across the mobile/desktop breakpoint (resolution$1.onChange ->
      connectSliderMediaEvents() -> destroySlider() + createMobileSlider() or
      createDesktopSlider()), so any breakpoint cross more than ~5s after page load
      permanently orphans this script from the live instance - neither the proximity
      walk nor a thumbnail-driven Flickity `change` re-subscribes, because thumbnails
      live outside the observed carousel element so the pointerdown/focusin backstop
      does not cover a thumbnail jump either. Fixed by restarting the ladder from the
      resize listener whenever it had already run out.

   FADE MODE (issue #465): theme.js builds the desktop/iPad slider (768px and up, every
   product template's `thumbnails-below@slides`) and the mobile "thumbs" slider with
   fade:true. flickity-fade stacks every cell in the selected cell's box and only
   changes opacity, so any geometry test calls every slide "on screen", and any
   hydrated slide's <img> loads at once. Measured on live 2026-09-30 (Pagosa 3-piece,
   246 media): the IntersectionObserver hydrated 243 of 243 slides at page load (820px:
   ~17MB); with it disabled, the first tap or click ran hydrateEverything() and loaded
   all 246 (1280px: 63MB, ~1GB decoded); a phone that tapped the gallery in portrait
   (slide mode) and then rotated into the landscape fade layout loaded all 246 (45MB).
   Flickity's selected index is the only real proximity signal in a stack, and it is
   never late: select() emits `change` synchronously, before the fade renders. So:
   1. The IntersectionObserver ignores entries while the gallery has a live Flickity
      slider (fade or slide), and re-observes still-deferred slides when the slider is
      torn down (a breakpoint cross into a stacked layout), so the observer reports
      that layout's real geometry. Slide mode needs this too: the sticky add-to-cart
      bar's copy of the gallery is built while hidden, so its cells sit collapsed in
      one box, and on live the bar appearing on a phone hydrated all 246 (16.5MB).
   2. Backstops 2 and 3 above, in any gallery that runs or can switch into a fade
      slider (canFade()), re-sync the Flickity binding and hydrate the selected window
      instead of every slide. Galleries with no fade mode at any breakpoint keep
      hydrateEverything() unchanged.
   3. The window wraps when the instance has wrapAround (theme.js sets it on both
      sliders): "previous" from the first slide, and flickity-fade's drag crossfade
      (getFadeDragWrapIndexes), both reach the last slide.
   4. Thumbnail intent (a 100ms hover, pointerdown, or focus on a
      [data-slideshow-thumbnail]) hydrates that thumbnail's slide ahead of the click,
      so a far jump starts its image request before Flickity selects it.
   5. Issue #471 (replaces #465's route hydration): in slide mode (phones) a jump of
      more than 2 slides (a color swatch, a page dot) snaps straight to the target
      instead of panning across, and downloading, every photo in between; the
      selected slide's unfinished image switches to loading="eager" +
      fetchpriority="high" so it starts at once. On a 1.6 Mbps phone the pan made the
      photo the shopper asked for wait 4-8 s behind the route photos. The sticky bar
      copy's image priority is left alone so it never competes with the main gallery.
   Zoom is unaffected: PhotoSwipe builds its items from [data-product-json]; getAlt()
   reads a gallery <img alt> only when one exists and otherwise falls back to the JSON
   alt or product title, which is the same text for every product that carries no
   `#group_value` alt token. */
(function () {
  'use strict';
  if (window.tcGalleryDefer) return;

  var SLIDESHOW_SELECTOR = '[data-product-slideshow]';
  var SLIDE_SELECTOR = '[data-media-slide]';
  var TEMPLATE_SELECTOR = 'template[data-deferred-media]';
  var ZOOM_BUTTON_SELECTOR = '[data-zoom-button]';
  var THUMB_SELECTOR = '[data-slideshow-thumbnail]';
  var MEDIA_WRAPPER_SELECTOR = '.product__media__wrapper';
  var STICKY_COPY_SELECTOR = '[data-sticky-media-wrapper]'; // aria-hidden mini copy in the sticky bar

  var HYDRATE_AHEAD = 2; // slides ahead/behind the selected index kept hydrated
  var THUMB_INTENT_MS = 100; // hover this long on a thumbnail = intent (skips sweeps)
  var SETTLE_DELAY_MS = 700; // mirrors __tcGalleryExposureSync's fallback pass
  var BIND_RETRY_MS = 250;
  var BIND_RETRY_LIMIT = 20; // ~5s, same bound used by __tcGalleryExposureSync

  // Flickity is NOT on window: assets/vendor.js exports everything on
  // window.themeVendor, with the constructor at themeVendor.Flickity.default. Resolve
  // both shapes, plus window.Flickity, exactly as __tcGalleryExposureSync does.
  function flickityCtor() {
    var tv = window.themeVendor;
    var F = window.Flickity || (tv && tv.Flickity && (tv.Flickity.default || tv.Flickity));
    return F && typeof F.data === 'function' ? F : null;
  }

  // The instance currently live on this element (F.data() drops it on destroy), unlike
  // setupInstance's `flkty`, which can still point at a destroyed one.
  function liveFlickity(carousel) {
    var F = flickityCtor();
    var live = F ? F.data(carousel) : null;
    return live && live.options ? live : null;
  }

  // flickity-fade adds .is-fade on activate and removes it on deactivate/destroy.
  function isFadeNow(carousel) {
    if (carousel.classList.contains('is-fade')) return true;
    var live = liveFlickity(carousel);
    return !!(live && live.options.fade);
  }

  // Any live Flickity instance, fade or slide (Flickity adds .flickity-enabled on
  // activate and removes it on destroy).
  function isSliderNow(carousel) {
    return carousel.classList.contains('flickity-enabled') || !!liveFlickity(carousel);
  }

  // Issue #465: does this gallery run a fade slider at any breakpoint? Mirrors theme.js
  // (Media reads both attributes, missing = "none"): createDesktopSlider() builds a
  // fade slider for any desktop style but "none"; createMobileSlider() builds none for
  // "none" and passes fade:true for every other mobile style except "slideshow" and
  // "carousel". A phone rotating into landscape crosses into the desktop slider, so a
  // slide-mode gallery with a desktop slideshow counts.
  function canFade(carousel) {
    var desktop = carousel.getAttribute('data-slideshow-desktop-style') || 'none';
    var mobile = carousel.getAttribute('data-slideshow-mobile-style') || 'none';
    return desktop !== 'none' ||
      (mobile !== 'none' && mobile !== 'slideshow' && mobile !== 'carousel');
  }

  function getSlides(carousel) {
    return Array.prototype.slice.call(carousel.querySelectorAll(SLIDE_SELECTOR));
  }

  // Idempotent: a slide with no <template> (already hydrated, or was one of the first
  // 3 real slides to begin with) is a silent no-op.
  function hydrateSlide(slide) {
    var tpl = slide.querySelector(TEMPLATE_SELECTOR);
    if (!tpl) return false;
    // A deferred slide's template sits inside an aspect-ratio sizer div
    // (media.liquid) so Flickity's adaptiveHeight measures the cell at its
    // real height at init. Hydration swaps the whole sizer for the content,
    // keeping the cell height constant across the swap.
    var sizer = tpl.parentElement;
    if (sizer && sizer.hasAttribute('data-tc-defer-sizer')) {
      sizer.replaceWith(tpl.content);
    } else {
      tpl.replaceWith(tpl.content);
    }
    return true;
  }

  function hydrateAll(carousel) {
    var changed = false;
    getSlides(carousel).forEach(function (slide) {
      if (hydrateSlide(slide)) changed = true;
    });
    return changed;
  }

  // `wrap` (issue #465): wrapAround carousels reach the far end from slide 0, so the
  // window continues from the other end instead of stopping at the edge.
  function hydrateNear(carousel, selectedIndex, wrap) {
    if (typeof selectedIndex !== 'number' || selectedIndex < 0) return false;
    var slides = getSlides(carousel);
    var count = slides.length;
    var changed = false;
    for (var offset = -HYDRATE_AHEAD; offset <= HYDRATE_AHEAD; offset++) {
      var i = selectedIndex + offset;
      if (wrap && count) {
        i = ((i % count) + count) % count;
      } else if (i < 0 || i >= count) {
        continue;
      }
      if (hydrateSlide(slides[i])) changed = true;
    }
    return changed;
  }

  // Slides Flickity would animate across from `from` to `to`: vendor.js _wrapSelect
  // goes the shorter way round when wrapping, direct on a tie.
  function routeLength(count, from, to, wrap) {
    var distance = Math.abs(to - from);
    if (wrap && distance > count / 2) distance = count - distance;
    return distance;
  }

  // Issue #471. Sets fetchpriority on a slide's image while it is still downloading;
  // a finished image is left untouched. Returns the image it changed, or null.
  function setImagePriority(slide, priority) {
    var img = slide && slide.querySelector('img');
    if (!img || (img.complete && img.naturalWidth > 0)) return null;
    img.setAttribute('fetchpriority', priority);
    return img;
  }

  // Pre-Flickity-boot (or a non-carousel [data-product-slideshow], e.g. a stacked
  // desktop layout) has no selectedIndex - fall back to whichever cell is already
  // marked selected, else assume the first slide.
  function currentIndex(carousel, flkty) {
    if (flkty && typeof flkty.selectedIndex === 'number') return flkty.selectedIndex;
    var slides = getSlides(carousel);
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].classList.contains('is-selected')) return i;
    }
    return 0;
  }

  function setupInstance(carousel) {
    if (carousel.__tcGalleryDeferBound) return;
    // Issue #337: skip the quickview drawer's gallery, because nothing in this file
    // tears an instance down. scan() re-runs on every `theme:quickview:media`, and
    // theme.js's getQuickviewHTML() rebuilds the drawer's whole subtree
    // (`modalContent.innerHTML = ...`) on every open, so each open handed
    // setupInstance() a fresh unbound element and permanently registered another window
    // resize listener (which also restarts the bindFlickity ladder), another window load
    // listener, another IntersectionObserver over slides the next open detaches, and
    // more timers - measured at +1 of each per open, unbounded.
    //
    // The drawer's gallery can never hold a deferred slide: it renders through
    // sections/api-product-quickview.liquid (class quickview__modal__images), which
    // passes no_defer: true, and snippets/media.liquid turns that into
    // render_real = true for every media item (added in PR #333's own browser gate,
    // because the drawer stacks its media with no carousel and so has no hydration
    // driver). With no template present hydrateSlide() returns false on every path, so
    // an instance there would only register listeners and do nothing observable. Both
    // conditions are required: the class confines the skip to the drawer, and the
    // template check keeps the skip safe if that section ever starts deferring.
    //
    // Not "any gallery without a template": since #466/#475 a color-grouped PDP gallery
    // can start with no template in the slider while the other colors wait as deferred
    // templates in the [data-tc-color-group-store] holder next to it, and those slides
    // need this instance once a swatch swaps them in.
    //
    // Deliberately does NOT set the bound flag when skipping, so a later scan() may
    // re-evaluate the element rather than caching a decision this file cannot re-check.
    if (carousel.classList.contains('quickview__modal__images') &&
        !carousel.querySelector(TEMPLATE_SELECTOR)) return;
    carousel.__tcGalleryDeferBound = true;

    var flkty = null;
    var bindTries = 0;
    var bindTimer = null;
    var settleTimer = null;
    var hydratedAll = false;

    var remeasureTimer = null;
    var remeasureRetries = 0;
    var REMEASURE_RETRY_MS = 60;
    var REMEASURE_RETRY_LIMIT = 15; // ~900ms bound, well past any real settle
    // Independent-QA fix (PR #333, FAIL at fd5ea07): Flickity's adaptiveHeight
    // caches each cell's outerHeight at slider init, when deferred cells were
    // still empty <template>s measuring 0 - so navigating into a
    // just-hydrated slide collapsed the mobile gallery to a ~10px strip and
    // threw the selected index off. Any hydration therefore schedules one
    // debounced flkty.resize(), which re-measures cells against the now-real
    // DOM (diagnostically confirmed by the QA run to restore the gallery).
    //
    // Codex gate fix (PR #333, FAIL at 2a44279): the hydration that most commonly
    // triggers this - the proximity walk reacting to Flickity's own `change` event -
    // fires from inside select(), BEFORE select() calls startAnimation() sets
    // isAnimating=true (vendor.js proto.select). Flickity's own resize() is a no-op
    // whenever isAnimating or isDragging is true (vendor.js proto.resize, the #1177
    // iOS 15 guard), so the very first attempt almost always lands mid-transition and
    // silently drops the remeasure. The next real idle checkpoint is `settle`, but by
    // then hydrateNear() finds nothing new (already hydrated by the `change` handler),
    // so sync() never calls remeasureSoon() again and the collapse this fix targets
    // can still reproduce. Retrying against flkty.isAnimating/isDragging (plain
    // instance properties, not private) until Flickity is actually idle - capped so a
    // stuck instance can never retry forever - closes that gap.
    function attemptRemeasure() {
      if (!flkty || typeof flkty.resize !== 'function') {
        remeasureRetries = 0;
        return;
      }
      if (flkty.isAnimating || flkty.isDragging) {
        if (remeasureRetries < REMEASURE_RETRY_LIMIT) {
          remeasureRetries++;
          remeasureTimer = setTimeout(attemptRemeasure, REMEASURE_RETRY_MS);
        } else {
          // Second independent-QA FAIL (1ecf705): a fast swipe burst keeps
          // Flickity animating past the ~900ms timer budget, and vendor
          // resize() no-ops under its own animating-guard, so a forced call
          // here was silently swallowed and nothing ever re-armed. Re-arm off
          // Flickity's own settle event instead - guaranteed to fire when the
          // animation actually ends, however long the burst lasts.
          remeasureRetries = 0;
          if (typeof flkty.once === 'function') {
            flkty.once('settle', attemptRemeasure);
          } else {
            remeasureTimer = setTimeout(attemptRemeasure, 250);
          }
        }
        return;
      }
      remeasureRetries = 0;
      try { flkty.resize(); } catch (e) { /* instance mid-teardown */ }
    }

    function remeasureSoon() {
      if (!flkty || typeof flkty.resize !== 'function') return;
      clearTimeout(remeasureTimer);
      remeasureRetries = 0;
      remeasureTimer = setTimeout(attemptRemeasure, 40);
    }

    var lastIndex = null; // selected index at the previous sync on the bound instance
    // hydratedAll (the non-fade backstop already hydrated every slide) skips only the
    // hydration work; the snap and the destination priority below still apply
    // (Codex P2 on #472).
    function sync() {
      var live = liveFlickity(carousel);
      var wrap = !!(live && live.options.wrapAround);
      var index = currentIndex(carousel, flkty);
      var changed = !hydratedAll && hydrateNear(carousel, index, wrap);
      // Issue #471 (replaces #465's route hydration): a slide-mode (non-fade) jump of
      // more than HYDRATE_AHEAD slides (a color swatch, a page dot) snaps straight to
      // the target instead of panning across every slide in between. That pan was a
      // half-second blur of photos that were unhydrated or still downloading, and each
      // one started a download the photo the shopper asked for then waited behind:
      // 4-8 s on a 1.6 Mbps phone, with the previous jump's route photos (up to 1.2 MB)
      // still in flight at the next tap. `change` fires inside select(), in the same
      // task as the first animation step, so the snap lands before anything paints.
      // Swipes, arrows and keyboard steps move one or two slides and keep their
      // animation; a drag's own select is never snapped. Fade mode already crossfades
      // straight to the target.
      if (live && live === flkty && lastIndex !== null && index !== lastIndex && !isFadeNow(carousel) &&
          !live.isDragSelect && !live.isDragging && typeof live.positionSliderAtSelected === 'function' &&
          routeLength(getSlides(carousel).length, lastIndex, index, wrap) > HYDRATE_AHEAD) {
        live.positionSliderAtSelected();
      }
      lastIndex = live && live === flkty ? index : null;
      prioritizeSelected(index);
      if (changed) remeasureSoon();
    }

    // Issue #471: the selected slide's image downloads now, ahead of anything else. A
    // lazy image far off-screen (a slide-mode jump) would otherwise wait until the pan
    // reaches it; loading="eager" starts the request immediately. A previous
    // destination the shopper jumped away from before it finished drops back to auto.
    var priorityImg = null;
    function prioritizeSelected(index) {
      if (carousel.closest(STICKY_COPY_SELECTOR)) return;
      var img = setImagePriority(getSlides(carousel)[index], 'high');
      if (priorityImg && priorityImg !== img && !(priorityImg.complete && priorityImg.naturalWidth > 0)) {
        priorityImg.setAttribute('fetchpriority', 'auto');
      }
      priorityImg = img;
      if (img && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
    }

    function syncSoonAndSettled() {
      sync();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(sync, SETTLE_DELAY_MS);
    }

    function unbindFlickity() {
      var old = flkty;
      flkty = null;
      if (!old || typeof old.off !== 'function') return;
      old.off('settle', sync);
      old.off('dragEnd', sync);
      old.off('change', sync);
    }

    // Rebind ladder: this script can parse before theme.js upgrades the carousel, and
    // theme.js destroys/recreates the Flickity instance across the mobile/desktop
    // breakpoint, so every tick is a rebind check, not a one-shot bind - identical
    // shape to __tcGalleryExposureSync's own bindFlickity().
    // Returns true when it picked up a new live instance.
    function rebind() {
      var F = flickityCtor();
      var found = F ? F.data(carousel) : null;
      if (found && typeof found.on === 'function' && found !== flkty) {
        unbindFlickity();
        flkty = found;
        lastIndex = null; // a rebuilt instance did not animate from the old index
        flkty.on('settle', sync);
        flkty.on('dragEnd', sync);
        flkty.on('change', sync);
        return true;
      }
      return false;
    }

    function bindFlickity() {
      if (rebind()) sync();
      if (bindTries++ < BIND_RETRY_LIMIT) {
        clearTimeout(bindTimer);
        bindTimer = setTimeout(bindFlickity, BIND_RETRY_MS);
      }
    }

    // PR #333 review fix: the ladder above is a one-shot - once bindTries exhausts
    // (~5s), nothing calls bindFlickity() again. theme.js's Media class destroys and
    // recreates its Flickity instance on this same carousel element whenever the
    // page crosses the mobile/desktop breakpoint (resolution$1.onChange ->
    // connectSliderMediaEvents()), which resize is exactly the signal for. Without
    // this, a breakpoint cross more than ~5s after page load permanently orphans
    // `flkty` from the live instance: sync()'s proximity walk goes stale, and
    // backstop #1 does not reliably cover the gap either (a thumbnail jump lives
    // outside this carousel element, so it never fires pointerdown/focusin here).
    function restartBindLadderIfExhausted() {
      if (bindTries < BIND_RETRY_LIMIT) return; // ladder still running, leave it alone
      bindTries = 0;
      clearTimeout(bindTimer);
      bindFlickity();
    }

    // Hard backstop #1: any user interaction with the gallery hydrates every
    // remaining slide immediately, so an interaction path that reaches a slide faster
    // than the proximity walk above (thumbnail jump, tab focus, a fast swipe) never
    // finds an inert <template>. Since issue #465 this is the path only for galleries
    // with no fade mode at any breakpoint; see backstop() below.
    function hydrateEverything() {
      if (hydratedAll) return;
      hydratedAll = true;
      if (hydrateAll(carousel)) remeasureSoon();
      if (slideObserver) slideObserver.disconnect();
    }

    // Issue #465: in a fade stack every hydrated slide loads its image, so a
    // fade-capable gallery's backstops re-sync the binding (in case the instance was
    // recreated since the ladder last ran) and hydrate the selected window instead.
    function backstop() {
      if (canFade(carousel) || isFadeNow(carousel)) {
        rebind();
        sync();
        return;
      }
      hydrateEverything();
    }
    ['pointerdown', 'touchstart', 'focusin'].forEach(function (evt) {
      carousel.addEventListener(evt, backstop, { passive: true });
    });

    // Hard backstop #3 (PR #333 review fix): the Flickity proximity walk above only
    // ever runs for carousels that HAVE a Flickity instance. The Stacked/Grid/Mosaic
    // desktop layouts never create one (theme.js's createDesktopSlider returns early
    // when desktopStyle=="none"), so currentIndex() always falls back to slide 0 and
    // sync() only ever hydrates DOM slides 0-2 - ordinary mouse-wheel scrolling past
    // slide 3 in one of those layouts left every later slide an inert <template> for
    // the rest of the visit, since scrolling fires none of backstop #1's events
    // either. An IntersectionObserver per slide covers this (and is a harmless,
    // idempotent no-op for carousel layouts, where the proximity walk usually gets
    // there first): a slide within rootMargin of the viewport hydrates regardless of
    // which layout mode is active or whether Flickity exists at all.
    //
    // Issue #465: except while a Flickity slider is live, where geometry lies. Fade
    // mode stacks every slide in one box; and a slider built while hidden (the
    // sticky add-to-cart bar's copy of this gallery, display:none until the shopper
    // scrolls) collapses every cell into its box until its next resize. Measured on
    // live: all 246 Pagosa slides hydrated through this observer on desktop load, and
    // again in the sticky copy when the bar appeared on a phone (16.5MB). With a live
    // slider, its selected index drives hydration (sync() above). Entries are
    // dropped while one is live; when it goes away (the class change below), the
    // still-deferred slides are re-observed, because an observer only reports
    // transitions and would otherwise stay silent about slides that were already
    // "visible" before and still are in the new layout.
    var slideObserver = null;
    if (typeof IntersectionObserver === 'function') {
      slideObserver = new IntersectionObserver(
        function (entries) {
          if (isSliderNow(carousel)) return;
          var changed = false;
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting && hydrateSlide(entries[i].target)) changed = true;
          }
          if (changed) remeasureSoon();
        },
        { root: null, rootMargin: '600px 0px', threshold: 0 }
      );
      getSlides(carousel).forEach(function (slide) {
        slideObserver.observe(slide);
      });

      var wasSlider = isSliderNow(carousel);
      new MutationObserver(function () {
        var slider = isSliderNow(carousel);
        if (wasSlider && !slider && !hydratedAll) {
          getSlides(carousel).forEach(function (slide) {
            if (!slide.querySelector(TEMPLATE_SELECTOR)) return;
            slideObserver.unobserve(slide);
            slideObserver.observe(slide);
          });
        }
        wasSlider = slider;
      }).observe(carousel, { attributes: true, attributeFilter: ['class'] });
    }

    // Hard backstop #2: a capture-phase listener on this gallery's own zoom buttons.
    // Belt-and-suspenders alongside backstop #1 (a click is preceded by pointerdown on
    // every input modality that fires pointer events) so the PhotoSwipe zoom modal -
    // whose click handler was bound once, at page init, directly to these same button
    // elements - never opens while a slide it could show is still a <template>.
    // Routed through backstop() (issue #465): the modal's images come from product
    // JSON either way, so a fade-capable gallery only needs its selected window.
    carousel.addEventListener(
      'click',
      function (event) {
        if (event.target && event.target.closest && event.target.closest(ZOOM_BUTTON_SELECTOR)) {
          backstop();
        }
      },
      true
    );

    // Issue #465, thumbnail intent: thumbnails sit outside the carousel element (none of
    // the listeners above see them), and a click on one selects its slide directly, so
    // hydrate that slide on the first sign of intent. Pointerdown and focus act at
    // once; hover waits THUMB_INTENT_MS so sweeping the pointer across the strip loads
    // nothing. The `change` walk still covers the click itself.
    var mediaWrapper = carousel.closest(MEDIA_WRAPPER_SELECTOR);
    if (mediaWrapper) {
      var intentTimer = null;
      var intentThumb = null;
      var thumbFrom = function (event) {
        var thumb = event.target && event.target.closest ? event.target.closest(THUMB_SELECTOR) : null;
        return thumb && mediaWrapper.contains(thumb) ? thumb : null;
      };
      var hydrateForThumb = function (thumb) {
        var id = thumb.getAttribute('data-media-select');
        if (!id) return;
        var slides = getSlides(carousel);
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === id) {
            if (hydrateSlide(slides[i])) remeasureSoon();
            return;
          }
        }
      };
      // over/out also fire between a thumbnail's own children; only a move onto a
      // different thumbnail (or off the strip) restarts or cancels the timer.
      mediaWrapper.addEventListener('pointerover', function (event) {
        var thumb = thumbFrom(event);
        if (thumb === intentThumb) return;
        clearTimeout(intentTimer);
        intentThumb = thumb;
        if (thumb) intentTimer = setTimeout(function () { hydrateForThumb(thumb); }, THUMB_INTENT_MS);
      });
      mediaWrapper.addEventListener('pointerout', function (event) {
        if (intentThumb && event.relatedTarget && intentThumb.contains(event.relatedTarget)) return;
        clearTimeout(intentTimer);
        intentThumb = null;
      });
      ['pointerdown', 'focusin'].forEach(function (evt) {
        mediaWrapper.addEventListener(evt, function (event) {
          var thumb = thumbFrom(event);
          if (thumb) hydrateForThumb(thumb);
        }, { passive: true });
      });
    }

    bindFlickity();
    syncSoonAndSettled();
    window.addEventListener('resize', function () {
      restartBindLadderIfExhausted();
      syncSoonAndSettled();
    });
    window.addEventListener('load', syncSoonAndSettled);
  }

  function scan(root) {
    (root || document).querySelectorAll(SLIDESHOW_SELECTOR).forEach(setupInstance);
  }

  function init() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme-editor section re-renders swap markup without executing inline scripts
  // (same landmine documented on __tcGalleryExposureSync above), so re-scan whatever
  // the editor just replaced.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) scan(e.target);
  });

  // The quickview drawer's [data-product-slideshow] markup does not exist at
  // DOMContentLoaded - per IMAGE-SPEC.md §2 (verified against assets/theme.js), the
  // Alpine quickview component clones its <script type="text/x-template"> content and
  // appends the built modal as a direct child of document.body on click. Watching only
  // document.body's own childList (not subtree) is the narrow, cheap equivalent of
  // tc-delivery-estimate.js's observeCart() pattern for this same class of
  // AJAX/clone-injected markup, without the cost of a page-wide subtree observer.
  var bodyObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches(SLIDESHOW_SELECTOR)) setupInstance(node);
        if (node.querySelectorAll) scan(node);
      }
    }
  });
  bodyObserver.observe(document.body, { childList: true });

  // PR #333 review fix: the bodyObserver above only sees document.body's OWN
  // childList. Confirmed against assets/theme.js: the quickview modal wrapper is
  // appended to document.body once, EMPTY, at Alpine init - that append IS caught by
  // bodyObserver, but scan() finds no [data-product-slideshow] yet because the
  // gallery markup doesn't exist. It arrives later, on click, via
  // `this.modalContent.innerHTML = fresh.querySelector('[data-api-content]').innerHTML`
  // inside getQuickviewHTML() - a mutation nested several levels under that
  // already-existing body child, invisible to a childList-only (non-subtree)
  // observer. Rather than pay for a page-wide subtree observer, listen for
  // theme.js's own `theme:quickview:media` CustomEvent, which getQuickviewHTML
  // dispatches (bubbling) on quickviewHolder immediately after that same innerHTML
  // assignment, and rescan. scan()/setupInstance() are idempotent no-ops for every
  // gallery already bound, so rescanning the whole document here is cheap and safe.
  document.addEventListener('theme:quickview:media', function () {
    scan(document);
  });

  window.tcGalleryDefer = { hydrateAll: hydrateAll, scan: scan };
})();
